import { useRouter } from "next/router";
import Link from "next/link";
import { ReactNode, AnchorHTMLAttributes, useMemo } from "react";
import React from "react";

interface CustomLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  children: ReactNode;
  prefetch?: boolean;
}

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

function getDisplayedPath(
  asPath: string,
  carro: string | string[] | undefined
) {
  const pathname = asPath.split("#")[0].split("?")[0] || "/";
  if (!pathname.includes("[")) return pathname;

  if (typeof carro === "string" && carro && !carro.includes("[")) {
    return `/${carro}`;
  }

  return pathname;
}

const CustomLink: React.FC<CustomLinkProps> = ({
  href,
  children,
  prefetch,
  ...props
}) => {
  const router = useRouter();

  const combinedHref = useMemo(() => {
    const [baseURL, hashFragment] = href.split("#");
    const [rawPath, newQueryString] = baseURL.split("?");

    const currentSearch = (router.asPath || "").split("#")[0].split("?")[1] || "";
    const currentParams = new URLSearchParams(currentSearch);
    const utmParams = new URLSearchParams();

    UTM_KEYS.forEach((key) => {
      const value = currentParams.get(key);
      if (value) utmParams.set(key, value);
    });

    const newParams = new URLSearchParams(newQueryString || "");
    const finalParams = new URLSearchParams(newParams.toString());
    utmParams.forEach((value, key) => {
      finalParams.set(key, value);
    });

    // Links só com âncora (#proposta) não têm caminho. O Next completa
    // com router.pathname (/[carro]) e o slug do veículo some da URL.
    const path = rawPath
      ? rawPath
      : getDisplayedPath(router.asPath || "/", router.query.carro);

    const queryStringFinal = finalParams.toString();
    let hrefWithParams = queryStringFinal
      ? `${path}?${queryStringFinal}`
      : path;

    if (hashFragment) {
      hrefWithParams += `#${hashFragment}`;
    }

    return hrefWithParams;
  }, [href, router.asPath, router.query.carro]);

  const isInPageHash = href.startsWith("#");

  if (isInPageHash) {
    return (
      <a href={combinedHref} {...props}>
        {children}
      </a>
    );
  }

  return (
    <Link href={combinedHref} prefetch={prefetch} {...props}>
      {children}
    </Link>
  );
};

export default React.memo(CustomLink);
